import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DriverApiService } from '@fleet/api/driver/driver-api.service';
import { NetworkGroupApiService } from '@fleet/api/network-group/network-group-api.service';
import { NetworkApiService } from '@fleet/api/network/network-api.service';

import { AuthService } from '@fleet/auth/services/auth.service';
import { LocaleService } from '@fleet/locale/locale-service/locale.service';
import {
  ApiResponse,
  AreaModel,
  DriverModel,
  NetworkGroupModel,
  NetworkGroupSearchResultModel,
  NetworkModel,
  OperatorModel,
  OrganisationUserModel,
  ReviewModel,
  TravellerModel,
} from '@fleet/model';
import { FleetNavigationService } from '@fleet/navigation';
import { ProductConfigurationService } from '@fleet/product-configuration';
import { NetworkGroupHeaderService } from '@fleet/token/network-group-header/network-group-header.service';
import { TranslocoService } from '@jsverse/transloco';
import { Settings } from 'luxon';
import { BehaviorSubject, combineLatest, forkJoin, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
  tap,
} from 'rxjs/operators';

interface NetworkGroupCache {
  [key: string]: NetworkGroupModel;
}

interface ServiceAreaCache {
  [key: string]: AreaModel;
}

@Injectable({
  providedIn: 'root',
})
export class NetworkGroupService {
  selectedGroup: BehaviorSubject<NetworkGroupModel> = new BehaviorSubject(null);
  outstandingDriverReviews: BehaviorSubject<ReviewModel[]> =
    new BehaviorSubject([]);
  serviceArea: BehaviorSubject<AreaModel> = new BehaviorSubject(null);
  fleetProduct: string;
  // New properties for network and available groups
  private network = new BehaviorSubject<NetworkModel>(null);
  private availableGroups = new BehaviorSubject<NetworkGroupModel[]>([]);

  // Cache for network groups and service areas
  private networkGroupCache: NetworkGroupCache = {};
  private serviceAreaCache: ServiceAreaCache = {};
  private networkCache: { [key: string]: NetworkModel } = {};

  // Track whether the network group has been initialized
  private networkGroupInitialized = new BehaviorSubject<boolean>(false);
  private aggregator = false;
  attributionNetworkGroup = new BehaviorSubject<NetworkGroupModel>(null);
  private attributionEntity = new BehaviorSubject<string>(null);

  constructor(
    private driverApiService: DriverApiService,
    private networkGroupApiService: NetworkGroupApiService,
    private networkApiService: NetworkApiService,
    private authService: AuthService,
    private productConfigurationService: ProductConfigurationService,
    private localeService: LocaleService,
    private translocoService: TranslocoService,
    private fleetNavigationService: FleetNavigationService,
    private networkGroupHeaderService: NetworkGroupHeaderService,
    @Inject('env') env: any
  ) {
    this.fleetProduct = env.fleetProduct;

    combineLatest([
      this.network$.pipe(filter((network) => !!network)),
      this.authService.user$.pipe(
        filter((user) => !!user && user.travellerId == null)
      ),
    ]).subscribe(([network, user]) => {
      // Search for available network groups when network changes
      this.networkGroupApiService
        .searchNetworkGroups({}, network.networkId)
        .subscribe({
          next: (
            resp: HttpResponse<ApiResponse<NetworkGroupModel[]>> | any
          ) => {
            const groups = resp?.body?.data;
            this.availableGroups.next(groups);
            //Check if all groups have the same timezone
            if (groups && groups.length > 0) {
              const allTimezones = groups
                .map((g: NetworkGroupSearchResultModel) => g.timezone)
                .filter(Boolean);
              const uniqueTimezones = Array.from(new Set(allTimezones));
              if (uniqueTimezones.length === 1) {
                Settings.defaultZone = uniqueTimezones[0] as string;
              }
            }
          },
          error: (err: any) => {
            // On error, clear available groups
            this.availableGroups.next([]);
            // Optionally log or handle error
            console.error('Error fetching available network groups:', err);
          },
        });
    });
    // lets initate the network and network group from the user and product configuration
    if (env.fleetProduct !== 'TRAVELLER') {
      combineLatest([
        this.productConfigurationService.networkConfig$,
        this.authService.user$.pipe(
          filter((user) => !!user),
          take(1)
        ),
      ]).subscribe(([networkConfig, user]) => {
        if (networkConfig?.networkId) {
          this.getNetwork(networkConfig.networkId);
        }
        const networkGroup =
          networkConfig.networkGroupId ||
          this.GetNetworkGroupByUser(user)?.networkGroupId;
        if (networkGroup) {
          this.getNetworkGroup(
            networkGroup,
            networkConfig.networkId,
            false,
            false
          );
        } else {
          if (this.fleetProduct === 'HUB') {
            this.aggregator = true;
          }
          this.setSelectedGroup(null, false);
        }
      });
    } else {
      //lets get the network from traveller based off product configuration
      this.productConfigurationService.networkConfig$.subscribe(
        (networkConfig) => {
          if (networkConfig && networkConfig.networkId) {
            this.getNetwork(networkConfig.networkId);
          }
          if (networkConfig && networkConfig.networkGroupId) {
            this.getNetworkGroup(
              networkConfig.networkGroupId,
              networkConfig.networkId,
              false,
              false
            );
          } else {
            this.setSelectedGroup(null, false);
          }
        }
      );
    }

    // Subscribe to both selected group and attribution group changes to update the header service
    combineLatest([
      this.selectedGroup.asObservable(),
      this.attributionNetworkGroup.asObservable(),
    ])
      .pipe(
        map(([selectedGroup, attributionGroup]) => {
          // Return attribution group ID if it exists, otherwise return selected group ID, or null if both are null
          return (
            attributionGroup?.networkGroupId ||
            selectedGroup?.networkGroupId ||
            null
          );
        }),
        distinctUntilChanged(),
        tap((networkGroupId) => {
          if (this.fleetProduct === 'HUB') {
            this.networkGroupHeaderService.networkGroupId.set(networkGroupId);
          }
        })
      )
      .subscribe();

    //subscribe to set locale and timezone
    this.initializedNetworkAndNetworkGroup$.subscribe(
      ({ networkGroup, network }) => {
        const locale = networkGroup?.locale || network?.locale;

        const lang = locale.replace(/-/g, '_').toLowerCase();
        const timezone = networkGroup?.timezone;
        if (this.translocoService.getActiveLang() !== lang) {
          this.translocoService.setActiveLang(lang);
        }
        if (this.localeService.getLocale() !== locale) {
          this.localeService.setLocale(locale);
        }
        if (timezone) {
          Settings.defaultZone = timezone;
        }
        if (locale) {
          Settings.defaultLocale = locale;
        }
      }
    );
  }

  getNetwork(networkId: string) {
    // Check for brand override query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const brandOverride = urlParams.get('brandOverride');

    // Check cache first before making API call
    if (this.networkCache[networkId]) {
      const cachedNetwork = this.networkCache[networkId];

      if (brandOverride === 'fleet' && cachedNetwork) {
        cachedNetwork.displayName = 'Fleet Demo';
      }

      this.network.next(cachedNetwork);
      return;
    }

    this.networkApiService.getNetwork(networkId).subscribe({
      next: (resp: ApiResponse<NetworkModel>) => {
        const networkData = resp.data;

        // Check for brand override query parameter
        const urlParams = new URLSearchParams(window.location.search);
        const brandOverride = urlParams.get('brandOverride');

        if (brandOverride === 'fleet' && networkData) {
          networkData.displayName = 'Fleet Demo';
        }

        // Cache the network data
        this.networkCache[networkId] = networkData;
        this.network.next(networkData);
      },
    });
  }

  get networkId() {
    return this.network.value?.networkId;
  }

  getNetworkGroupOrAttributionNetworkGroup() {
    if (this.attributionNetworkGroup.value) {
      return this.attributionNetworkGroup.value;
    }
    return this.selectedGroup.value;
  }

  get attributionNetworkGroup$() {
    return this.attributionNetworkGroup.asObservable();
  }

  get attributionEntityAndGroup$() {
    return combineLatest([
      this.attributionEntity.asObservable(),
      this.attributionNetworkGroup$,
    ]);
  }

  setSelectedGroup(group: NetworkGroupModel, searchServiceAreas: boolean) {
    if (group) {
      if (this.fleetProduct === 'HUB') {
        this.fleetNavigationService.setCurrentGroupByNetworkGroup(group);
      }

      if (group.type !== 'TAXI_NETWORK') {
        // Clear service area if not a taxi network
        this.serviceArea.next(null);
      }
    } else {
      if (this.fleetProduct === 'HUB') {
        this.fleetNavigationService.setCurrentGroup(null);
      }

      // Handle null group - reset to defaults
      this.serviceArea.next(null);
    }

    // Always update the group and initialization state before searchServiceAreas
    // so searchServiceAreas reads the new selectedGroup.value
    this.selectedGroup.next(group);
    this.networkGroupInitialized.next(true);

    if (group && searchServiceAreas) {
      this.searchServiceAreas();
    }
  }

  // Search for service areas based on current network group
  // Should only be called when user selects a group in switcher (not full screen)
  searchServiceAreas() {
    this.serviceArea.next(null);
    const group = this.selectedGroup.value;
    const networkId = this.network.value?.networkId;

    if (!networkId) {
      return;
    }

    // Create a cache key for service area
    const cacheKey = `${networkId}${
      group?.networkGroupId ? '-' + group.networkGroupId : ''
    }`;

    // Check if service area is already in cache
    if (this.serviceAreaCache[cacheKey]) {
      this.serviceArea.next(this.serviceAreaCache[cacheKey]);
      return;
    }

    this.networkApiService
      .searchServiceAreas(networkId, {
        ...(group?.networkGroupId && { networkGroupId: group.networkGroupId }),
        includePolygon: true,
      })
      .subscribe({
        next: (resp: HttpResponse<ApiResponse<AreaModel[]>> | any) => {
          const serviceArea =
            resp.body.data.length > 0 ? resp.body.data[0] : null;

          // Cache the service area
          if (serviceArea) {
            this.serviceAreaCache[cacheKey] = serviceArea;
          }

          this.serviceArea.next(serviceArea);
        },
        error: (err: any) => {
          console.error('Error fetching service areas:', err);
          this.serviceArea.next(null);
        },
      });
  }

  getNetworkGroup(
    networkGroupId: string,
    networkId: string,
    isAttribution: boolean,
    searchServiceAreas: boolean
  ) {
    // Create a cache key for the network group
    const cacheKey = `${networkId}-${networkGroupId}`;

    // Check if network group is already in cache
    if (this.networkGroupCache[cacheKey]) {
      if (isAttribution) {
        this.attributionNetworkGroup.next(this.networkGroupCache[cacheKey]);
      } else {
        this.setSelectedGroup(
          this.networkGroupCache[cacheKey],
          searchServiceAreas
        );
      }
      return;
    }

    this.networkGroupApiService
      .getNetworkGroup(networkGroupId, networkId)
      .subscribe({
        next: (resp: ApiResponse<NetworkGroupModel>) => {
          // Cache the network group
          this.networkGroupCache[cacheKey] = resp.data;
          if (isAttribution) {
            this.attributionNetworkGroup.next(resp.data);
          } else {
            this.setSelectedGroup(resp.data, searchServiceAreas);
          }
        },
      });
  }

  // Clear cache for specific network group or all if no params provided
  clearNetworkGroupCache(networkId?: string, networkGroupId?: string) {
    if (networkId && networkGroupId) {
      const cacheKey = `${networkId}-${networkGroupId}`;
      delete this.networkGroupCache[cacheKey];
    } else {
      this.networkGroupCache = {};
    }
  }

  // Clear cache for specific service area or all if no params provided
  clearServiceAreaCache(networkId?: string, networkGroupId?: string) {
    if (networkId) {
      const cacheKey = `${networkId}${
        networkGroupId ? '-' + networkGroupId : ''
      }`;
      delete this.serviceAreaCache[cacheKey];
    } else {
      this.serviceAreaCache = {};
    }
  }

  // Clear all caches
  clearAllCaches() {
    this.networkGroupCache = {};
    this.serviceAreaCache = {};
    this.networkCache = {};
  }

  get networkGroup$() {
    return this.selectedGroup.asObservable();
  }

  // Observable that only emits when network group is initialized
  get initializedNetworkAndNetworkGroup$(): Observable<{
    network: NetworkModel;
    networkGroup: NetworkGroupModel;
  }> {
    return combineLatest([
      this.selectedGroup.asObservable(),
      this.networkGroupInitialized.asObservable(),
      this.network.asObservable(),
    ]).pipe(
      filter(([_, initialized, network]) => {
        return initialized == true && network != null;
      }),
      map(([networkGroup, _, network]) => ({ networkGroup, network })),
      distinctUntilChanged((prev, curr) => {
        return (
          prev.networkGroup?.networkGroupId ===
            curr.networkGroup?.networkGroupId &&
          prev.network?.networkId === curr.network?.networkId
        );
      })
    );
  }

  // New observable that combines initializedNetworkAndNetworkGroup$ and attributionNetworkGroup$
  get activeNetworkGroup$(): Observable<NetworkGroupModel> {
    return combineLatest([
      this.initializedNetworkAndNetworkGroup$,
      this.attributionNetworkGroup$,
    ]).pipe(
      map(([{ networkGroup }, attributionGroup]) => {
        // Return attribution group if it exists, otherwise return the regular network group
        return attributionGroup || networkGroup;
      }),
      distinctUntilChanged((prev, curr) => {
        return prev?.networkGroupId === curr?.networkGroupId;
      })
    );
  }
  get networkAndActiveNetworkGroup$(): Observable<{
    network: NetworkModel;
    networkGroup: NetworkGroupModel;
  }> {
    return combineLatest([this.network$, this.activeNetworkGroup$]).pipe(
      map(([network, networkGroup]) => ({ network, networkGroup }))
    );
  }

  // Check if network group has been initialized
  get isNetworkGroupInitialized(): boolean {
    return this.networkGroupInitialized.value;
  }

  // Observable for initialization state
  get networkGroupInitialized$(): Observable<boolean> {
    return this.networkGroupInitialized.asObservable();
  }

  get networkGroupId() {
    return this.selectedGroup.value
      ? this.selectedGroup.value.networkGroupId
      : null;
  }

  get regionCode() {
    return this.selectedGroup.value
      ? this.selectedGroup.value.regionCode
      : null;
  }

  get serviceArea$() {
    return combineLatest([
      this.serviceArea.asObservable().pipe(startWith(null)),
      this.productConfigurationService.serviceArea$,
    ]).pipe(
      map(([networkGroupServiceArea, productConfigServiceArea]) => {
        // Use networkGroupService serviceArea first, fall back to productConfigurationService serviceArea
        return networkGroupServiceArea || productConfigServiceArea;
      }),
      distinctUntilChanged((prev, curr) => {
        // Compare by areaId if both have it, otherwise compare by reference
        if (prev?.areaId && curr?.areaId) {
          return prev.areaId === curr.areaId;
        }
        return prev === curr;
      })
    );
  }

  get networkGroupAndServiceArea$() {
    return combineLatest([this.networkGroup$, this.serviceArea$]).pipe(
      map(([group, area]) => {
        return { networkGroup: group, serviceArea: area };
      })
    );
  }

  get networkGroupAndServiceArea() {
    return {
      networkGroup: this.selectedGroup.value,
      serviceArea: this.serviceArea.value,
    };
  }

  get searchLatLngAndCountryCode$() {
    return this.networkGroupAndServiceArea$.pipe(
      map((groupAndArea: any) => {
        if (groupAndArea.networkGroup && groupAndArea.serviceArea) {
          return {
            latitude: groupAndArea.serviceArea.point.latitude,
            longitude: groupAndArea.serviceArea.point.longitude,

            countryCode: groupAndArea.networkGroup.countryCode,
          };
        } else {
          return null;
        }
      })
    );
  }

  fetchGroupState(networkGroupId: string, networkId: string) {
    const calls = [
      this.driverApiService.searchReviews({
        networkGroupId: networkGroupId,
        networkId: networkId,
        status: 'PENDING,IN_PROGRESS',
      }),
    ];
    forkJoin(calls).subscribe({
      next: ([reviewResp]) => {
        this.outstandingDriverReviews.next(reviewResp.data);
      },
    });
  }

  get networkGroupTimezone() {
    return this.selectedGroup.value && this.selectedGroup.value.timezone
      ? this.selectedGroup.value.timezone
      : null;
  }

  // Getters for new observables
  get network$(): Observable<NetworkModel> {
    return this.network.asObservable();
  }

  get availableGroups$(): Observable<NetworkGroupModel[]> {
    return this.availableGroups.asObservable();
  }

  get hasAvailableGroups$(): Observable<boolean> {
    return this.availableGroups.pipe(
      map((groups) => groups && groups.length > 0)
    );
  }

  /**
   * Returns a distinct list of timezones from all available network groups
   * @returns Observable of distinct timezone strings
   */
  get distinctTimezones$(): Observable<string[]> {
    return this.availableGroups$.pipe(
      map((groups) => {
        if (!groups || groups.length === 0) {
          return [];
        }
        const timezones = groups.map((group) => group.timezone).filter(Boolean); // Filter out null/undefined timezones
        return Array.from(new Set(timezones)); // Return distinct timezones
      })
    );
  }

  /**
   * Returns a distinct list of timezones from all available network groups (synchronous)
   * @returns Array of distinct timezone strings
   */
  getDistinctTimezones(): string[] {
    const groups = this.availableGroups.value;
    if (!groups || groups.length === 0) {
      return [];
    }
    const timezones = groups.map((group) => group.timezone).filter(Boolean); // Filter out null/undefined timezones
    return Array.from(new Set(timezones)); // Return distinct timezones
  }

  /**
   * Returns the current timezone - either the selected network group's timezone
   * or the first distinct timezone from available groups if no group is selected
   * @returns Observable of current timezone string
   */
  get currentTimezone$(): Observable<string> {
    return combineLatest([
      this.selectedGroup.asObservable(),
      this.distinctTimezones$,
    ]).pipe(
      map(([selectedGroup, distinctTimezones]) => {
        // If a network group is selected, use its timezone
        if (selectedGroup?.timezone) {
          return selectedGroup.timezone;
        }

        // If no group is selected but we have available timezones, return the first one
        if (distinctTimezones.length > 0) {
          return distinctTimezones[0];
        }

        // Fallback to null if no timezone available
        return null;
      }),
      distinctUntilChanged()
    );
  }

  // Observable for locale based on networkGroup.locale or network.locale
  get locale$(): Observable<string> {
    return this.initializedNetworkAndNetworkGroup$.pipe(
      map(({ networkGroup, network }) => {
        return networkGroup?.locale || network?.locale || 'en-AU';
      }),
      distinctUntilChanged()
    );
  }

  setAttributionNetworkGroup(
    networkId: string,
    networkGroupId: string,
    entity: string
  ) {
    if (this.aggregator) {
      this.attributionEntity.next(entity);
      this.getNetworkGroup(networkGroupId, networkId, true, false);
    }
  }

  setAttributionGroup(networkGroupId: string, networkId: string) {
    this.attributionNetworkGroup.next({
      networkGroupId,
      networkId,
    } as NetworkGroupModel);
  }

  clearGroupIfAggregator() {
    if (this.fleetProduct === 'HUB' && this.aggregator) {
      this.attributionEntity.next(null);
      this.attributionNetworkGroup.next(null);
    }
  }

  /**
   * Gets network group information based on user model type
   * @param user - Can be DriverModel, OperatorModel, OrganisationUserModel, or TravellerModel
   * @returns Object containing networkId and networkGroupId if found, null otherwise
   */
  GetNetworkGroupByUser(
    user: DriverModel | OperatorModel | OrganisationUserModel | TravellerModel
  ): { networkId: string; networkGroupId: string } | null {
    if (!user) {
      return null;
    }

    // Determine the type of user model by checking for unique properties
    switch (true) {
      case 'driverId' in user && 'network' in user: {
        // DriverModel
        const driver = user as DriverModel;
        if (driver.network?.networkId && driver.network?.networkGroupId) {
          return {
            networkId: driver.network.networkId,
            networkGroupId: driver.network.networkGroupId,
          };
        }
        break;
      }

      case 'operatorId' in user &&
        'networkId' in user &&
        'networkGroupId' in user: {
        // OperatorModel
        const operator = user as OperatorModel;
        if (operator.networkId && operator.networkGroupId) {
          return {
            networkId: operator.networkId,
            networkGroupId: operator.networkGroupId,
          };
        }
        break;
      }

      case 'organisationUserId' in user: {
        // OrganisationUserModel
        const orgUser = user as OrganisationUserModel;
        // OrganisationUserModel has organisationGroupId but no direct network group info
        // Would need additional lookup to get network group from organisation group
        console.warn(
          'OrganisationUserModel does not contain direct network group information'
        );
        return null;
      }

      case 'travellerId' in user && 'network' in user: {
        // TravellerModel
        const traveller = user as TravellerModel;
        if (traveller.network?.networkId && traveller.network?.networkGroupId) {
          return {
            networkId: traveller.network.networkId,
            networkGroupId: traveller.network.networkGroupId,
          };
        }
        break;
      }

      default:
        console.warn('Unknown user model type', {
          user,
          keys: user ? Object.keys(user) : [],
        });
        return null;
    }

    return null;
  }
}
